import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { PublicHeader, PublicFooter } from "@/components/layouts/PublicLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Lock, Eye, EyeOff, CheckCircle, XCircle, ArrowLeft } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_712ba195-f8b2-4b91-a610-23f4aedf7ae0/artifacts/i0b4179m_A_I_Means_Business.jpg";

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setIsVerifying(false);
        setIsValidToken(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/auth/verify-reset-token`, {
          params: { token }
        });
        
        setIsValidToken(response.data.valid);
        if (response.data.email) {
          setUserEmail(response.data.email);
        }
      } catch (error) {
        setIsValidToken(false);
      } finally {
        setIsVerifying(false);
      }
    };
    
    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password) {
      toast.error("Please enter a new password");
      return;
    }
    
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    setIsLoading(true);
    
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      
      setIsSuccess(true);
      toast.success("Password reset successfully!");
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate("/login");
      }, 3000);
      
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to reset password. The link may have expired.";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state
  if (isVerifying) {
    return (
      <div className="min-h-screen bg-background">
        <PublicHeader />
        <section className="py-24 bg-gradient-to-b from-navy-950 to-navy-900">
          <div className="max-w-md mx-auto px-6">
            <div className="bg-white p-8 rounded-2xl shadow-2xl text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
              <p className="text-slate-600">Verifying reset link...</p>
            </div>
          </div>
        </section>
        <PublicFooter />
      </div>
    );
  }

  // Invalid token state
  if (!isValidToken) {
    return (
      <div className="min-h-screen bg-background">
        <PublicHeader />
        <section className="py-24 bg-gradient-to-b from-navy-950 to-navy-900">
          <div className="max-w-md mx-auto px-6">
            <div className="bg-white p-8 rounded-2xl shadow-2xl">
              <div className="flex justify-center mb-6">
                <img src={LOGO_URL} alt="A.I. Means Business" className="h-16 w-auto" />
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-8 h-8 text-red-600" />
                </div>
                <h2 className="font-heading text-xl font-bold text-navy-900 mb-2">
                  Invalid or Expired Link
                </h2>
                <p className="text-slate-600 text-sm mb-6">
                  This password reset link is invalid or has expired. Please request a new one.
                </p>
                <Link to="/forgot-password">
                  <Button className="bg-primary hover:bg-primary/90 text-white">
                    Request New Link
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
        <PublicFooter />
      </div>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <div className="min-h-screen bg-background">
        <PublicHeader />
        <section className="py-24 bg-gradient-to-b from-navy-950 to-navy-900">
          <div className="max-w-md mx-auto px-6">
            <div className="bg-white p-8 rounded-2xl shadow-2xl">
              <div className="flex justify-center mb-6">
                <img src={LOGO_URL} alt="A.I. Means Business" className="h-16 w-auto" />
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="font-heading text-xl font-bold text-navy-900 mb-2">
                  Password Reset Complete
                </h2>
                <p className="text-slate-600 text-sm mb-6">
                  Your password has been successfully reset. You will be redirected to the login page shortly.
                </p>
                <Link to="/login">
                  <Button className="bg-primary hover:bg-primary/90 text-white">
                    Sign In Now
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />

      <section className="py-24 bg-gradient-to-b from-navy-950 to-navy-900">
        <div className="max-w-md mx-auto px-6">
          {/* Card - White background with dark text */}
          <div className="bg-white p-8 rounded-2xl shadow-2xl">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <img 
                src={LOGO_URL} 
                alt="A.I. Means Business" 
                className="h-16 w-auto"
              />
            </div>

            {/* Header */}
            <div className="text-center mb-6">
              <h1 className="font-heading text-2xl font-bold text-navy-900 mb-2">
                Reset Password
              </h1>
              <p className="text-slate-600 text-sm">
                Enter your new password for <strong>{userEmail}</strong>
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="password" className="text-navy-900 text-sm font-medium">
                  New Password
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="bg-slate-50 border-slate-300 text-navy-900 pr-10 focus:border-primary focus:ring-primary"
                    autoComplete="new-password"
                    data-testid="reset-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-navy-900"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Must be at least 8 characters
                </p>
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-navy-900 text-sm font-medium">
                  Confirm Password
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="bg-slate-50 border-slate-300 text-navy-900 pr-10 focus:border-primary focus:ring-primary"
                    autoComplete="new-password"
                    data-testid="reset-password-confirm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-navy-900"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-primary hover:bg-primary/90 text-white"
                data-testid="reset-password-submit"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    Reset Password
                  </>
                )}
              </Button>
            </form>

            {/* Back to Login */}
            <div className="text-center mt-6">
              <Link 
                to="/login" 
                className="text-primary text-sm hover:underline inline-flex items-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Sign In
              </Link>
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default ResetPasswordPage;
